import { useState } from 'react';
import Box from '@mui/material/Box';

import {
  Button,
  Dialog as ElementsDialog,
  Typography,
} from '@zendesk/forethought-elements';
import { RestorationContent, RestorationLoading } from './RestorationContent';
import { hasUnrestorableItems } from './utils';
import { getTextByType } from 'src/components/global-workflow-builder/dialogUtils';
import {
  useLazyGetRestorationRequirementsQuery,
  usePublishWorkflowVersionMutation,
} from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { useAppDispatch } from 'src/store/hooks';
import { RestorationRequirements } from 'src/types/workflowBuilderAPITypes';

/** Minimal version data needed for publish dialog - works with both AutoflowVersionHistorySummary and AutoflowIntentWorkflowResponse */
interface VersionData {
  last_updated_at?: string | null;
  version: number;
  version_name?: string;
  version_type: WorkflowVersionType;
}

interface PublishVersionDialogProps {
  intentWorkflowId?: string;
  mode: 'publish' | 'republish';
  onClose: () => void;
  onPublishSuccess?: () => void;
  open: boolean;
  versionData?: VersionData | null;
}

type DialogState =
  | { type: 'initial' }
  | { type: 'checking' }
  | { requirements: RestorationRequirements; type: 'restoration' }
  | { requirements: RestorationRequirements; type: 'cannot_restore' };

export const PublishVersionDialog = ({
  intentWorkflowId,
  mode,
  onClose,
  onPublishSuccess,
  open,
  versionData,
}: PublishVersionDialogProps) => {
  const dispatch = useAppDispatch();
  const [publishWorkflowVersion, { isLoading: isPublishing }] =
    usePublishWorkflowVersionMutation({
      // Shared so observers (e.g. FlowDiagramPanel) can detect publish-in-flight
      // and suppress noise like the out-of-sync banner.
      fixedCacheKey: 'publish-workflow-version',
    });
  const [getRestorationRequirements] = useLazyGetRestorationRequirementsQuery();
  const [dialogState, setDialogState] = useState<DialogState>({
    type: 'initial',
  });

  const handleClose = () => {
    setDialogState({ type: 'initial' });
    onClose();
  };

  const { bodyText, buttonText, titleText, toastText } =
    getTextByType('publish-version');

  const executePublish = async () => {
    if (!intentWorkflowId || !versionData) {
      return;
    }

    try {
      await publishWorkflowVersion({
        body: {
          version: versionData.version,
          version_type: versionData.version_type,
        },
        intentWorkflowId,
      }).unwrap();

      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 3000,
          title: toastText,
          variant: 'main',
        }),
      );

      handleClose();
      onPublishSuccess?.();
    } catch (error) {
      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 3000,
          title: 'Failed to publish version',
          variant: 'danger',
        }),
      );
    }
  };

  const handlePublish = async () => {
    if (!intentWorkflowId || !versionData) {
      return;
    }

    // If we're already showing restoration dialog, proceed with publish
    // (publish endpoint now handles restoration automatically)
    if (dialogState.type === 'restoration') {
      await executePublish();
      return;
    }

    // For republish mode, check restoration requirements first to inform user
    if (mode === 'republish') {
      setDialogState({ type: 'checking' });

      try {
        const requirements = await getRestorationRequirements({
          intentWorkflowId,
          version: versionData.version,
          versionType: versionData.version_type,
        }).unwrap();

        // Show restoration info to user if needed, but always use publish endpoint
        if (requirements.needs_restoration) {
          // Check if any items cannot be restored
          if (hasUnrestorableItems(requirements)) {
            setDialogState({ requirements, type: 'cannot_restore' });
            return;
          }
          setDialogState({ requirements, type: 'restoration' });
          return;
        }

        // No restoration needed, proceed directly with publish
        setDialogState({ type: 'initial' });
        await executePublish();
      } catch (error) {
        // On error fetching requirements, show error and reset state
        console.error('Failed to check restoration requirements:', error);
        setDialogState({ type: 'initial' });
        dispatch(
          setGlobalToastOptions({
            autoHideDuration: 3000,
            title: 'Failed to check restoration requirements',
            variant: 'danger',
          }),
        );
      }
    } else {
      // For regular publish, just publish directly
      await executePublish();
    }
  };

  const renderContent = () => {
    if (dialogState.type === 'checking') {
      return <RestorationLoading />;
    }

    if (dialogState.type === 'cannot_restore') {
      return (
        <RestorationContent
          cannotRestore
          requirements={dialogState.requirements}
        />
      );
    }

    if (dialogState.type === 'restoration') {
      return (
        <RestorationContent
          introText={bodyText}
          requirements={dialogState.requirements}
        />
      );
    }

    return (
      <Box sx={{ minWidth: '100%', whiteSpace: 'pre-line', width: 300 }}>
        <Typography variant='font14'>{bodyText}</Typography>
      </Box>
    );
  };

  const isLoading = isPublishing || dialogState.type === 'checking';
  const cannotRestore = dialogState.type === 'cannot_restore';

  const getDialogTitle = () => {
    if (cannotRestore) {
      return 'Cannot publish version';
    }
    return titleText ?? 'Publish agent';
  };

  return (
    <ElementsDialog
      footer={
        <>
          <Button disabled={isLoading} onClick={handleClose} variant='ghost'>
            {cannotRestore ? 'Close' : 'Cancel'}
          </Button>
          {!cannotRestore && (
            <Button
              disabled={dialogState.type === 'checking'}
              isLoading={isPublishing}
              onClick={handlePublish}
              variant='main'
            >
              {buttonText}
            </Button>
          )}
        </>
      }
      hideCloseButton
      onClose={handleClose}
      open={open}
      title={getDialogTitle()}
      variant='sm'
    >
      {renderContent()}
    </ElementsDialog>
  );
};
